import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { COVERAGE_BY_SLUG } from '../lib/coverage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'hcp_admin_token';

const STAGES = [
  'New Lead',
  'Basic Info Collected',
  'Full Profile Started',
  'Full Profile Completed',
  'Quote in Progress',
  'Quote Sent',
  'Follow-Up Needed',
  'Won',
  'Lost / Not Ready',
  'Annual Review',
];

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [leads, setLeads] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [completionCounts, setCompletionCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ coverage_type: '', stage: '', completion_status: '', request_type: '', language: '' });
  const [extraCounts, setExtraCounts] = useState({ callback: 0, spanish: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [testEmailResult, setTestEmailResult] = useState(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword('');
    } catch (err) {
      setLoginError(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setLeads([]);
  };

  const loadLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (filter.coverage_type) params.coverage_type = filter.coverage_type;
      if (filter.stage) params.stage = filter.stage;
      if (filter.completion_status) params.completion_status = filter.completion_status;
      if (filter.request_type) params.request_type = filter.request_type;
      if (filter.language) params.language = filter.language;
      const res = await axios.get(`${API}/admin/leads`, { headers: authHeaders(), params });
      setLeads(res.data.leads || []);
      setStageCounts(res.data.stage_counts || {});
      setCompletionCounts(res.data.completion_counts || {});
      setExtraCounts({ callback: res.data.callback_count || 0, spanish: res.data.spanish_count || 0 });
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [token, filter, authHeaders]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const updateStage = async (leadId, stage) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, { stage }, { headers: authHeaders() });
      await loadLeads();
      if (selected && selected.id === leadId) {
        setSelected((s) => ({ ...s, stage }));
      }
      toast.success(`Stage updated → ${stage}`);
    } catch (err) {
      toast.error('Failed to update stage: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const sendTestEmail = async () => {
    setTestEmailResult(null);
    const p = axios.post(`${API}/admin/test-email`, {}, { headers: authHeaders() });
    toast.promise(p, {
      loading: 'Sending test email…',
      success: (res) => `Test email sent via ${res.data?.provider || 'gmail'}`,
      error: (err) => 'Test email failed: ' + (err?.response?.data?.detail || err.message),
    });
    try {
      const res = await p;
      setTestEmailResult(res.data);
    } catch (err) {
      setTestEmailResult({ success: false, error: err?.response?.data?.detail || err.message });
    }
  };

  const stopFollowups = async (leadId) => {
    try {
      await axios.post(`${API}/admin/leads/${leadId}/stop-followups`, {}, { headers: authHeaders() });
      toast.success('Follow-ups stopped for this lead');
      await loadLeads();
      if (selected && selected.id === leadId) {
        setSelected((s) => ({ ...s, follow_up_status: 'stopped', next_follow_up_due: null }));
      }
    } catch (err) {
      toast.error('Could not stop follow-ups: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const runFollowupsNow = async () => {
    const p = axios.post(`${API}/admin/run-followups`, {}, { headers: authHeaders() });
    toast.promise(p, {
      loading: 'Running follow-up scan…',
      success: (res) => `Follow-up scan: ${res.data.sent}/${res.data.checked} sent`,
      error: 'Follow-up scan failed',
    });
    try { await p; await loadLeads(); } catch (e) { /* toast already shown */ }
  };

  // ===== LOGIN VIEW =====
  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 bg-[#f6f5f1]">
        <form onSubmit={login} className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md" data-testid="admin-login-form">
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#01325B' }}>Protection Center Admin</h1>
          <p className="text-sm text-gray-600 mb-6">Sign in to view leads and email delivery logs.</p>
          <label className="block text-sm font-medium text-gray-700 mb-2">Admin Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E07A2C] focus:border-transparent outline-none"
            placeholder="Enter admin password"
          />
          {loginError && (
            <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="admin-login-error">
              {loginError}
            </div>
          )}
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-btn"
            className="w-full mt-5 py-3 rounded-lg text-white font-semibold disabled:opacity-60"
            style={{ background: '#E07A2C' }}
          >
            {loggingIn ? 'Signing in...' : 'Sign In'}
          </button>
          <details className="mt-6 text-xs text-gray-500 cursor-pointer">
            <summary className="font-semibold">How to change this password?</summary>
            <div className="mt-2 leading-relaxed bg-gray-50 p-3 rounded">
              The admin password is set via the <code className="bg-white px-1 rounded">ADMIN_PASSWORD</code> env var in <code className="bg-white px-1 rounded">/app/backend/.env</code>.
              Update the value and run <code className="bg-white px-1 rounded">sudo supervisorctl restart backend</code>.
              <br /><br />
              <strong className="text-red-600">⚠️ Change this before going fully live.</strong>
            </div>
          </details>
        </form>
      </div>
    );
  }

  // ===== DASHBOARD VIEW =====
  return (
    <div className="bg-[#f6f5f1] min-h-[80vh]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#01325B' }}>Lead Dashboard</h1>
            <p className="text-sm text-gray-600">{total} total leads in the system</p>
          </div>
          <div className="flex gap-2">
            <button onClick={sendTestEmail} data-testid="send-test-email-btn"
              className="px-4 py-2 rounded-lg border-2 text-sm font-semibold" style={{ borderColor: '#01325B', color: '#01325B' }}>
              Send test email
            </button>
            <button onClick={runFollowupsNow} data-testid="run-followups-btn"
              className="px-4 py-2 rounded-lg border-2 text-sm font-semibold" style={{ borderColor: '#01325B', color: '#01325B' }}>
              Run follow-ups now
            </button>
            <a
              href={`${API}/admin/leads/export.csv`}
              onClick={(e) => {
                e.preventDefault();
                axios.get(`${API}/admin/leads/export.csv`, { headers: authHeaders(), responseType: 'blob' })
                  .then((res) => {
                    const blob = new Blob([res.data], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `hcp_leads_${Date.now()}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  })
                  .catch(() => alert('CSV export failed'));
              }}
              data-testid="export-csv-btn"
              className="px-4 py-2 rounded-lg border-2 text-sm font-semibold cursor-pointer" style={{ borderColor: '#01325B', color: '#01325B' }}>
              Export CSV
            </a>
            <button onClick={loadLeads} data-testid="refresh-leads-btn"
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#01325B' }}>
              Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#9c2a2a' }}>
              Log out
            </button>
          </div>
        </div>

        {testEmailResult && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${testEmailResult.success ? 'bg-green-50 border border-green-300 text-green-800' : 'bg-red-50 border border-red-300 text-red-800'}`} data-testid="test-email-result">
            Test email result: {testEmailResult.success ? `✅ sent via ${testEmailResult.provider}` : `❌ failed — ${JSON.stringify(testEmailResult.attempts || testEmailResult.error)}`}
          </div>
        )}

        {/* Stage counts */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          {STAGES.slice(0, 10).map((s) => (
            <div key={s} className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-500">{s}</div>
              <div className="text-xl font-bold" style={{ color: '#01325B' }}>{stageCounts[s] || 0}</div>
            </div>
          ))}
        </div>

        {/* Completion status row */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { key: 'basic', label: 'Basic Only' },
            { key: 'full_started', label: 'Full Profile Started' },
            { key: 'full_completed', label: 'Full Profile Completed' },
          ].map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter((f) => ({ ...f, completion_status: f.completion_status === c.key ? '' : c.key }))}
              className={`text-left bg-white rounded-lg p-3 border-2 transition-colors ${filter.completion_status === c.key ? 'border-[#E07A2C]' : 'border-gray-200 hover:border-gray-300'}`}
              data-testid={`completion-card-${c.key}`}
            >
              <div className="text-xs text-gray-500">{c.label}</div>
              <div className="text-xl font-bold" style={{ color: '#01325B' }}>{completionCounts[c.key] || 0}</div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="quick-filter-chips">
          <FilterChip
            active={filter.request_type === 'callback'}
            onClick={() => setFilter((f) => ({ ...f, request_type: f.request_type === 'callback' ? '' : 'callback' }))}
            testId="filter-chip-callback"
            label={`📞 Callback (${extraCounts.callback})`}
          />
          <FilterChip
            active={filter.language === 'es'}
            onClick={() => setFilter((f) => ({ ...f, language: f.language === 'es' ? '' : 'es' }))}
            testId="filter-chip-spanish"
            label={`Español (${extraCounts.spanish})`}
          />
        </div>
        <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Coverage Type</label>
            <select
              value={filter.coverage_type}
              onChange={(e) => setFilter((f) => ({ ...f, coverage_type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="filter-coverage"
            >
              <option value="">All</option>
              {Object.values(COVERAGE_BY_SLUG).map((c) => (
                <option key={c.slug} value={c.slug}>{c.emoji} {c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Stage</label>
            <select
              value={filter.stage}
              onChange={(e) => setFilter((f) => ({ ...f, stage: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="filter-stage"
            >
              <option value="">All</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Completion</label>
            <select
              value={filter.completion_status}
              onChange={(e) => setFilter((f) => ({ ...f, completion_status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="filter-completion"
            >
              <option value="">All</option>
              <option value="basic">Basic Only</option>
              <option value="full_started">Full Profile Started</option>
              <option value="full_completed">Full Profile Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Request Type</label>
            <select
              value={filter.request_type}
              onChange={(e) => setFilter((f) => ({ ...f, request_type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="filter-request-type"
            >
              <option value="">All</option>
              <option value="quote">Quote</option>
              <option value="callback">Callback</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Language</label>
            <select
              value={filter.language}
              onChange={(e) => setFilter((f) => ({ ...f, language: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="filter-language"
            >
              <option value="">All</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
            </select>
          </div>
          {Object.values(filter).some(Boolean) && (
            <button onClick={() => setFilter({ coverage_type: '', stage: '', completion_status: '', request_type: '', language: '' })} className="text-sm text-[#E07A2C] underline" data-testid="clear-filters-btn">Clear</button>
          )}
        </div>

        {/* Leads table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-[#01325B] text-white">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Coverage</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Lang</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">ZIP</th>
                  <th className="text-left px-3 py-2">Stage</th>
                  <th className="text-left px-3 py-2">Emails</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="10" className="text-center py-6 text-gray-500">Loading...</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan="10" className="text-center py-6 text-gray-500">No leads yet.</td></tr>
                )}
                {leads.map((l) => {
                  const meta = COVERAGE_BY_SLUG[l.coverage_type] || { emoji: '❔', label: l.coverage_type };
                  const name = `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.contact_name || l.business_name || '—';
                  const internalOk = l?.email_status?.internal?.success;
                  const prospectOk = l?.email_status?.prospect?.success;
                  return (
                    <tr key={l.id} className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{meta.emoji} <span className="hidden md:inline">{l.campaign_product === 'bundle' ? 'Home + Auto Bundle' : meta.label}</span></td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs" data-testid={`lead-type-${l.id}`}>
                        {l.request_type === 'callback'
                          ? <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 font-semibold">Callback</span>
                          : <span className="text-gray-600">Quote</span>}
                      </td>
                      <td className="px-3 py-2 text-xs" data-testid={`lead-lang-${l.id}`}>
                        {l.language === 'es'
                          ? <span className="px-2 py-0.5 rounded-full bg-[#01325B] text-white font-semibold">ES</span>
                          : <span className="text-gray-600">EN</span>}
                      </td>
                      <td className="px-3 py-2 font-medium">{name}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{l.phone}</td>
                      <td className="px-3 py-2">{l.email}</td>
                      <td className="px-3 py-2">{l.zip_code}</td>
                      <td className="px-3 py-2">
                        <select
                          value={l.stage || ''}
                          onChange={(e) => { e.stopPropagation(); updateStage(l.id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                          data-testid={`stage-select-${l.id}`}
                        >
                          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span title="Internal notification" className={internalOk ? 'text-green-700' : 'text-red-600'}>I:{internalOk ? '✓' : '✗'}</span>{' '}
                        {l.email
                          ? <span title="Prospect confirmation" className={prospectOk ? 'text-green-700' : 'text-red-600'}>P:{prospectOk ? '✓' : '✗'}</span>
                          : <span title="No email on file" className="text-gray-500">P:n/a</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail drawer */}
        {selected && (
          <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelected(null)}>
            <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-drawer">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs text-gray-500">Lead</div>
                  <h2 className="text-xl font-bold" style={{ color: '#01325B' }}>
                    {(COVERAGE_BY_SLUG[selected.coverage_type]?.emoji || '')} {COVERAGE_BY_SLUG[selected.coverage_type]?.label || selected.coverage_type}
                  </h2>
                </div>
                <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-900 text-2xl">&times;</button>
              </div>
              <Detail row={['Name', `${selected.first_name || ''} ${selected.last_name || ''}`.trim() || selected.contact_name || selected.business_name]} />
              {selected.business_name && <Detail row={['Business', selected.business_name]} />}
              <Detail row={['Phone', selected.phone]} />
              <Detail row={['Email', selected.email]} />
              <Detail row={['ZIP', selected.zip_code]} />
              {selected.property_address && <Detail row={['Property', selected.property_address]} />}
              {selected.rental_address && <Detail row={['Rental Address', selected.rental_address]} />}
              {selected.age_range && <Detail row={['Age Range', selected.age_range]} />}
              {selected.main_reason && <Detail row={['Main Reason', selected.main_reason]} />}
              {selected.coverage_interest && <Detail row={['Coverage Interest', selected.coverage_interest]} />}
              {selected.turning_65_soon && <Detail row={['Turning 65 soon?', selected.turning_65_soon]} />}
              {selected.already_on_medicare && <Detail row={['Already on Medicare?', selected.already_on_medicare]} />}
              <Detail row={['Best Time', selected.best_time_to_contact]} />
              <Detail row={['Source', selected.source]} />
              <Detail row={['Request Type', selected.request_type === 'callback' ? 'Callback' : 'Quote']} />
              <Detail row={['Language', selected.language === 'es' ? 'Spanish — respond in Spanish' : 'English']} />
              {selected.campaign_product && <Detail row={['Campaign Product', selected.campaign_product === 'bundle' ? 'Home + Auto Bundle' : selected.campaign_product]} />}
              {selected.current_carrier && <Detail row={['Current Carrier', selected.current_carrier]} />}
              {selected.policy_expiration_date && <Detail row={['Policy Expiration', selected.policy_expiration_date]} />}
              {selected.preferred_contact_method && <Detail row={['Preferred Contact', selected.preferred_contact_method]} />}
              {selected.utm && Object.values(selected.utm).some(Boolean) && (
                <Detail row={['UTM', Object.entries(selected.utm).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' · ')]} />
              )}
              {selected.consent_record && (
                <Detail row={['Consent', `Contact: ${selected.consent_record.contact ? 'Yes' : 'No'} · Email mktg: ${selected.consent_record.marketing_email ? 'Yes' : 'No'} · SMS mktg: ${selected.consent_record.marketing_sms ? 'Yes' : 'No'} (${selected.consent_record.disclosure_version || '—'})`]} />
              )}
              <Detail row={['Status', selected.completion_status]} />
              <Detail row={['Stage', selected.stage]} />
              <Detail row={['Created', new Date(selected.created_at).toLocaleString()]} />
              {selected.updated_at && (
                <Detail row={['Last Updated', new Date(selected.updated_at).toLocaleString()]} />
              )}

              {/* Follow-up status */}
              <div className="mt-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
                <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#01325B' }}>Follow-up Pipeline</div>
                <div className="text-xs flex flex-wrap gap-x-4 gap-y-1">
                  <span>Status: <strong>{selected.follow_up_status || 'pending'}</strong></span>
                  <span>Sent: <strong>{selected.follow_up_count || 0}/3</strong></span>
                  {selected.last_follow_up_sent && (
                    <span>Last: {new Date(selected.last_follow_up_sent).toLocaleString()}</span>
                  )}
                  {selected.next_follow_up_due && (
                    <span>Next due: {new Date(selected.next_follow_up_due).toLocaleString()}</span>
                  )}
                </div>
                {selected.follow_up_status === 'pending' && (
                  <button
                    onClick={() => stopFollowups(selected.id)}
                    className="mt-2 text-xs text-red-600 underline"
                    data-testid="stop-followups-btn"
                  >
                    Stop follow-ups for this lead
                  </button>
                )}
              </div>
              {selected.notes && (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 mb-1">Notes</div>
                  <div className="text-sm bg-gray-50 border border-gray-200 rounded p-3">{selected.notes}</div>
                </div>
              )}

              {/* Full profile data */}
              {selected.full_profile && Object.keys(selected.full_profile).length > 0 && (
                <div className="mt-6 pt-4 border-t-2 border-[#E07A2C]">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: '#E07A2C' }}>
                    Full Protection Profile
                  </div>
                  <FullProfileView fp={selected.full_profile} />
                </div>
              )}

              <div className="mt-6 pt-3 border-t text-xs text-gray-500">
                Internal email: {selected?.email_status?.internal?.success ? `✅ via ${selected.email_status.internal.provider}` : `❌ failed`}<br />
                Prospect email: {selected?.email_status?.prospect?.success ? `✅ via ${selected.email_status.prospect.provider}` : (selected?.email_status?.prospect ? `❌ failed` : '—')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FilterChip = ({ active, onClick, testId, label }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className={`px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${active ? 'bg-[#E07A2C] border-[#E07A2C] text-white' : 'bg-white border-gray-300 text-[#01325B] hover:border-[#E07A2C]'}`}
  >
    {label}
  </button>
);

const Detail = ({ row }) => (
  <div className="flex border-b border-gray-100 py-2 text-sm">
    <div className="w-40 text-gray-500">{row[0]}</div>
    <div className="flex-1 font-medium" style={{ color: '#01325B' }}>{row[1] || '—'}</div>
  </div>
);

const prettyKey = (k) =>
  k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const FullProfileView = ({ fp }) => {
  // Render arrays (drivers, vehicles) as cards; everything else as Detail rows
  const entries = Object.entries(fp);
  const scalars = entries.filter(([, v]) => !Array.isArray(v) && typeof v !== 'object');
  const arrays = entries.filter(([, v]) => Array.isArray(v));

  return (
    <div>
      {scalars.map(([k, v]) => {
        let display = v;
        if (typeof v === 'boolean') display = v ? 'Yes' : 'No';
        return <Detail key={k} row={[prettyKey(k), String(display)]} />;
      })}
      {arrays.map(([k, arr]) => (
        <div key={k} className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#01325B' }}>{prettyKey(k)}</div>
          <div className="space-y-2">
            {arr.map((item, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">#{idx + 1}</div>
                {Object.entries(item || {}).map(([ik, iv]) => (
                  iv ? (
                    <div key={ik} className="flex text-xs py-0.5">
                      <div className="w-32 text-gray-500">{prettyKey(ik)}</div>
                      <div className="flex-1 font-medium" style={{ color: '#01325B' }}>{String(iv)}</div>
                    </div>
                  ) : null
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Admin;
